// useConversations — the Phase-1 state layer for direct messages: the inbox, the
// active conversation's messages, optimistic + idempotent send, and live updates
// via the SSE stream. Keeps a per-conversation message cache so switching threads
// doesn't refetch. Deliberately small — spaces/reactions/Dana layer on later.
import { useCallback, useEffect, useRef, useState } from 'react';
import { conversationsApi } from '../api/conversationsApi';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  readHighWaterMark, shouldAdvanceRead, shouldAcknowledgeDelivery,
  type ConversationReceipts,
} from '../readState';

// Long enough that scrolling a backlog settles into one call, short enough that
// the other side sees a blue tick while still looking at the screen.
const READ_DEBOUNCE_MS = 600;
// The grey -> blue latency for the sender. One cheap query (two MINs over the
// participant rows) per open, visible conversation.
const RECEIPT_POLL_MS = 5000;
import { useConversationStream } from '../api/useConversationStream';
import type { ConversationSummary, Message, MessageGif, MessagePoll, NewPoll } from '../types';
import { afterPage, initialHistory, shouldLoadOlder, type HistoryState } from '../messageHistory';
import type { RowAction } from '../rowActions';

const PAGE = 40;

function newClientId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

/** Merge a message into a list: replace an optimistic twin (same client_message_id)
 *  or an existing id, else append; keep ascending by seq (0 = optimistic, last). */
function mergeMessage(list: Message[], msg: Message): Message[] {
  const idx = list.findIndex(
    (m) => m.id === msg.id || (!!msg.client_message_id && m.client_message_id === msg.client_message_id),
  );
  const next = idx >= 0 ? list.map((m, i) => (i === idx ? msg : m)) : [...list, msg];
  return next.sort((a, b) => a.seq - b.seq || a.created_at.localeCompare(b.created_at));
}

export interface UseConversations {
  inbox: ConversationSummary[];
  inboxLoading: boolean;
  activeId: string | null;
  setActiveId: (id: string | null) => void;
  messages: Message[];
  messagesLoading: boolean;
  /** Fetch the page before the oldest message held. Prepends only; the caller
   *  restores the scroll position. */
  loadOlder: () => Promise<void>;
  /** Whether older messages remain for the open conversation. */
  canLoadOlder: boolean;
  /** A history page is in flight. */
  loadingOlder: boolean;
  /** Aggregate receipt cursors for the open conversation; undefined = not known
   *  yet, which is also the honest state for pre-feature messages. */
  receipts?: ConversationReceipts;
  streamStatus: 'connecting' | 'open' | 'closed';
  sendMessage: (body: string, mentionUserIds?: string[]) => Promise<void>;
  sendGif: (gif: MessageGif) => Promise<void>;
  sendPoll: (poll: NewPoll) => Promise<void>;
  sendFile: (file: File, caption?: string) => Promise<void>;
  createAssignment: (input: { title: string; description?: string; priority?: string; dueAt?: string; assigneeEmployeeId?: string; assigneeName?: string }) => Promise<void>;
  votePoll: (messageId: string, optionIds: string[]) => Promise<void>;
  toggleReaction: (messageId: string, key: string) => Promise<void>;
  editMessage: (messageId: string, body: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  toggleFavorite: (conversationId: string) => Promise<void>;
  /** Get a conversation off my list: hide it for me (remove), leave a circle, or
   *  archive one I own. Optimistically drops the row; a failure resyncs the inbox
   *  and rethrows so the caller can surface the backend's own message. */
  applyRowAction: (conversationId: string, action: RowAction) => Promise<void>;
  reload: () => void;
}

export function useConversations(activeIdFromUrl: string | null, currentUserId: string | undefined): UseConversations {
  const [inbox, setInbox] = useState<ConversationSummary[]>([]);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [byConv, setByConv] = useState<Record<string, Message[]>>({});
  // Older-message pagination, per conversation. The API and the server have
  // always supported it; nothing asked. See messageHistory.ts.
  const [history, setHistory] = useState<Record<string, HistoryState>>({});
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [streamStatus, setStreamStatus] = useState<'connecting' | 'open' | 'closed'>('connecting');
  const activeId = activeIdFromUrl;
  const activeRef = useRef<string | null>(activeId);
  activeRef.current = activeId;
  // Latest inbox without re-creating callbacks on every message.
  const inboxRef = useRef<ConversationSummary[]>(inbox);
  inboxRef.current = inbox;

  const loadInbox = useCallback(async () => {
    setInboxLoading(true);
    try {
      setInbox(await conversationsApi.inbox());
    } finally {
      setInboxLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInbox();
  }, [loadInbox]);

  // Load (once) the active conversation's recent history.
  useEffect(() => {
    if (!activeId || byConv[activeId]) return;
    let cancelled = false;
    setMessagesLoading(true);
    conversationsApi
      .messages(activeId, { limit: PAGE })
      .then((page) => {
        if (cancelled) return;
        // history comes newest-first; store ascending.
        const asc = [...page.messages].sort((a, b) => a.seq - b.seq);
        setByConv((prev) => ({ ...prev, [activeId]: asc }));
        // The cursor for the page BEFORE this one. Without keeping it, a thread
        // could only ever show its newest page.
        setHistory((prev) => ({ ...prev, [activeId]: afterPage(page.next_before_seq) }));
      })
      .finally(() => !cancelled && setMessagesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activeId, byConv]);

  // Live stream: append incoming messages to their conversation; bump the inbox.
  useConversationStream({
    onStatusChange: setStreamStatus,
    onMessage: useCallback(
      (msg: Message) => {
        setByConv((prev) => {
          const existing = prev[msg.conversation_id];
          // Only grow a thread we've already opened (avoids unbounded caches for
          // conversations the user hasn't looked at — the inbox still updates).
          if (!existing && msg.conversation_id !== activeRef.current) return prev;
          return { ...prev, [msg.conversation_id]: mergeMessage(existing ?? [], msg) };
        });
        setInbox((prev) =>
          prev.map((c) =>
            c.id === msg.conversation_id
              ? {
                  ...c,
                  last_message_seq: Math.max(c.last_message_seq, msg.seq),
                  last_message_at: msg.created_at,
                  unread_hint:
                    msg.conversation_id === activeRef.current || msg.sender_user_id === currentUserId
                      ? c.unread_hint
                      : c.unread_hint + 1,
                }
              : c,
          ),
        );
      },
      [currentUserId],
    ),
  });

  // Reaction liveness reconcile. SSE delivers new messages instantly, but reactions
  // (and edits/deletes) carry NO durable event (spec §10.1 realtime-only), so another
  // viewer's reaction wouldn't otherwise surface until a manual reload. Refetch the
  // open thread on a gentle interval (only while the tab is visible) and merge:
  // mergeMessage replaces by id, updating reactions in place, and never drops the
  // optimistic messages (no server twin yet) — so no flicker and no scroll jump unless
  // a genuinely new message arrived. Mobile already polls (4s); this is the web parity.
  useEffect(() => {
    if (!activeId) return;
    let stopped = false;
    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const page = await conversationsApi.messages(activeId, { limit: PAGE });
        if (stopped) return;
        setByConv((prev) => {
          let next = prev[activeId] ?? [];
          for (const m of page.messages) next = mergeMessage(next, m);
          return { ...prev, [activeId]: next };
        });
      } catch {
        /* transient — the next tick retries */
      }
    };
    const h = window.setInterval(tick, 12000);
    return () => {
      stopped = true;
      window.clearInterval(h);
    };
  }, [activeId]);

  // ── Receipts (slice 2) ────────────────────────────────────────────────────
  // Three separate jobs, deliberately not one effect: acknowledge what we have
  // RECEIVED, report what the user has actually READ, and pull back what the
  // other side has done so our own ticks can turn blue.

  // The tab being foregrounded. Loading a conversation into state while the tab
  // sits behind another one is not reading it.
  const [tabVisible, setTabVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState === 'visible',
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = () => setTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  // Highest seq we have reported, per conversation. Refs, not state: these must
  // not retrigger the effects that read them, or a 12s reconcile would write a
  // mark and emit an event on every tick.
  const sentReadRef = useRef<Record<string, number>>({});
  const sentDeliveredRef = useRef<Record<string, number>>({});

  const highWater = activeId ? readHighWaterMark(byConv[activeId] ?? []) : 0;

  // DELIVERED — not gated on visibility. The client has the message whether or
  // not anyone is looking at the tab, and that is exactly the difference
  // between two grey ticks and two blue ones.
  useEffect(() => {
    if (!activeId) return;
    const already = sentDeliveredRef.current[activeId] ?? 0;
    if (!shouldAcknowledgeDelivery(highWater, already)) return;
    sentDeliveredRef.current[activeId] = highWater;
    void conversationsApi.acknowledgeDelivery(activeId, highWater).catch(() => {
      // Let a later tick retry rather than stranding the cursor on one failure.
      sentDeliveredRef.current[activeId] = already;
    });
  }, [activeId, highWater]);

  // READ — only while the conversation is open AND the tab is foregrounded.
  // Debounced, so scrolling through a backlog settles into ONE high-water call
  // instead of one per message that lands.
  useEffect(() => {
    if (!activeId) return;
    const already = sentReadRef.current[activeId] ?? 0;
    if (!shouldAdvanceRead({ visible: tabVisible, conversationOpen: true, seq: highWater, lastSent: already })) {
      return;
    }
    const h = window.setTimeout(() => {
      sentReadRef.current[activeId] = highWater;
      void conversationsApi.advanceReadCursor(activeId, highWater).catch(() => {
        sentReadRef.current[activeId] = already;
      });
      setInbox((prev) => prev.map((c) => (c.id === activeId ? { ...c, unread_hint: 0 } : c)));
    }, READ_DEBOUNCE_MS);
    return () => window.clearTimeout(h);
  }, [activeId, highWater, tabVisible]);

  // FANOUT — the other side's progress, so my own ticks turn blue without a
  // refresh. A short poll rather than a second SSE cursor: typing and presence
  // already work this way, and it is the one mechanism that also serves mobile,
  // which has no SSE at all. One query per tick for a whole screen of bubbles.
  const [receipts, setReceipts] = useState<Record<string, ConversationReceipts>>({});
  useEffect(() => {
    if (!activeId || !tabVisible) return;
    let stopped = false;
    const pull = async () => {
      try {
        const r = await conversationsApi.receipts(activeId);
        if (!stopped) setReceipts((prev) => ({ ...prev, [activeId]: r }));
      } catch {
        /* transient — the next tick retries */
      }
    };
    void pull(); // immediate, so opening a thread does not wait a full interval
    const h = window.setInterval(pull, RECEIPT_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(h);
    };
  }, [activeId, tabVisible]);

  // Offline send queue: a failed send is kept (bubble marked "not sent") and its
  // payload parked here, then auto-retried on reconnect / on a slow sweep — so a
  // dropped connection self-heals (spec: "a send queue that survives offline and
  // retries"). A successful (re)send replaces the bubble by client_message_id.
  const pendingRef = useRef<Map<string, { convId: string; text: string; mentions?: string[]; gif?: MessageGif; replyTo?: string }>>(new Map());

  const attemptSend = useCallback(async (convId: string, clientId: string, text: string, mentions?: string[], gif?: MessageGif, replyTo?: string) => {
    try {
      const saved = await conversationsApi.postMessage(convId, {
        bodyPlain: text,
        clientMessageId: clientId,
        mentionUserIds: mentions && mentions.length ? mentions : undefined,
        gif,
        replyToMessageId: replyTo,
      });
      pendingRef.current.delete(clientId);
      setByConv((prev) => ({ ...prev, [convId]: mergeMessage(prev[convId] ?? [], saved) }));
    } catch (e) {
      // A server REFUSAL (4xx: too long, forbidden, bad request) is final — tell the
      // user why and drop the bubble; retrying would just fail again. Only a
      // network / 5xx failure goes into the offline retry queue.
      const status = (e as { response?: { status?: number } })?.response?.status ?? 0;
      if (status >= 400 && status < 500) {
        toast.error(getApiErrorMessage(e, 'Message could not be sent'));
        setByConv((prev) => ({ ...prev, [convId]: (prev[convId] ?? []).filter((m) => m.client_message_id !== clientId) }));
        return;
      }
      pendingRef.current.set(clientId, { convId, text, mentions, gif, replyTo });
      setByConv((prev) => ({
        ...prev,
        [convId]: (prev[convId] ?? []).map((m) =>
          m.client_message_id === clientId ? { ...m, send_state: 'failed' } : m,
        ),
      }));
    }
  }, []);

  const flushPending = useCallback(() => {
    pendingRef.current.forEach((p, clientId) => void attemptSend(p.convId, clientId, p.text, p.mentions, p.gif, p.replyTo));
  }, [attemptSend]);

  useEffect(() => {
    const onOnline = () => flushPending();
    window.addEventListener('online', onOnline);
    const iv = window.setInterval(() => {
      if (pendingRef.current.size) flushPending();
    }, 20000);
    return () => {
      window.removeEventListener('online', onOnline);
      window.clearInterval(iv);
    };
  }, [flushPending]);

  const sendMessage = useCallback(
    async (body: string, mentionUserIds?: string[], replyToMessageId?: string) => {
      const text = body.trim();
      const convId = activeRef.current;
      if (!text || !convId || !currentUserId) return;
      const clientId = newClientId();
      const optimistic: Message = {
        id: `optimistic-${clientId}`,
        conversation_id: convId,
        seq: Number.MAX_SAFE_INTEGER, // sort to the bottom until the server seq lands
        send_state: 'sending' as const,
        client_message_id: clientId,
        sender_user_id: currentUserId,
        kind: 'TEXT',
        body_plain: text,
        created_at: new Date().toISOString(),
        reply_to_message_id: replyToMessageId,
      };
      setByConv((prev) => ({ ...prev, [convId]: mergeMessage(prev[convId] ?? [], optimistic) }));
      await attemptSend(convId, clientId, text, mentionUserIds, undefined, replyToMessageId);
    },
    [currentUserId, attemptSend],
  );

  const sendGif = useCallback(
    async (gif: MessageGif) => {
      const convId = activeRef.current;
      if (!convId || !currentUserId || !gif?.url) return;
      const clientId = newClientId();
      const optimistic: Message = {
        id: `optimistic-${clientId}`,
        conversation_id: convId,
        seq: Number.MAX_SAFE_INTEGER,
        send_state: 'sending' as const,
        client_message_id: clientId,
        sender_user_id: currentUserId,
        kind: 'TEXT',
        body_plain: '',
        created_at: new Date().toISOString(),
        gif,
      };
      setByConv((prev) => ({ ...prev, [convId]: mergeMessage(prev[convId] ?? [], optimistic) }));
      await attemptSend(convId, clientId, '', undefined, gif);
    },
    [currentUserId, attemptSend],
  );

  const sendPoll = useCallback(
    async (poll: NewPoll) => {
      const convId = activeRef.current;
      if (!convId || !currentUserId || !poll.question.trim()) return;
      const clientId = newClientId();
      const optimistic: Message = {
        id: `optimistic-${clientId}`,
        conversation_id: convId,
        seq: Number.MAX_SAFE_INTEGER,
        send_state: 'sending' as const,
        client_message_id: clientId,
        sender_user_id: currentUserId,
        kind: 'TEXT',
        body_plain: '',
        created_at: new Date().toISOString(),
        poll: {
          question: poll.question,
          options: poll.options.map((o, i) => ({ id: `opt${i + 1}`, text: o.text })),
          multi: poll.multi,
          counts: {},
          my_votes: [],
        },
      };
      setByConv((prev) => ({ ...prev, [convId]: mergeMessage(prev[convId] ?? [], optimistic) }));
      try {
        const saved = await conversationsApi.postMessage(convId, { bodyPlain: '', clientMessageId: clientId, poll });
        setByConv((prev) => ({ ...prev, [convId]: mergeMessage(prev[convId] ?? [], saved) }));
      } catch {
        setByConv((prev) => ({
          ...prev,
          [convId]: (prev[convId] ?? []).map((m) =>
            m.client_message_id === clientId ? { ...m, send_state: 'failed' } : m,
          ),
        }));
      }
    },
    [currentUserId],
  );

  const sendFile = useCallback(
    async (file: File, caption?: string) => {
      const convId = activeRef.current;
      if (!convId || !currentUserId || !file) return;
      const clientId = newClientId();
      const optimistic: Message = {
        id: `optimistic-${clientId}`,
        conversation_id: convId,
        seq: Number.MAX_SAFE_INTEGER,
        send_state: 'sending' as const,
        client_message_id: clientId,
        sender_user_id: currentUserId,
        kind: 'FILE',
        body_plain: caption?.trim() || '',
        created_at: new Date().toISOString(),
        file: { key: '', name: file.name, mime: file.type, size: file.size },
      };
      setByConv((prev) => ({ ...prev, [convId]: mergeMessage(prev[convId] ?? [], optimistic) }));
      try {
        const saved = await conversationsApi.uploadAttachment(convId, file, { caption, clientMessageId: clientId });
        setByConv((prev) => ({ ...prev, [convId]: mergeMessage(prev[convId] ?? [], saved) }));
      } catch (e) {
        toast.error(getApiErrorMessage(e, 'File could not be sent'));
        setByConv((prev) => ({
          ...prev,
          [convId]: (prev[convId] ?? []).map((m) =>
            m.client_message_id === clientId ? { ...m, send_state: 'failed' } : m,
          ),
        }));
      }
    },
    [currentUserId],
  );

  const createAssignment = useCallback(
    async (input: { title: string; description?: string; priority?: string; dueAt?: string; assigneeEmployeeId?: string; assigneeName?: string }) => {
      const convId = activeRef.current;
      if (!convId || !input.title.trim()) return;
      const saved = await conversationsApi.createAssignment(convId, input); // throws on 403 → caller surfaces
      setByConv((prev) => ({ ...prev, [convId]: mergeMessage(prev[convId] ?? [], saved) }));
    },
    [],
  );

  const votePoll = useCallback(
    async (messageId: string, optionIds: string[]) => {
      const convId = activeRef.current;
      if (!convId) return;
      const before = byConv[convId] ?? [];
      const msg = before.find((m) => m.id === messageId);
      if (!msg?.poll) return;
      const applyOptimistic = (p: MessagePoll): MessagePoll => {
        const counts = { ...(p.counts ?? {}) };
        const oldMine = new Set(p.my_votes ?? []);
        const newMine = new Set(optionIds);
        for (const o of p.options) {
          const was = oldMine.has(o.id);
          const now = newMine.has(o.id);
          if (was && !now) counts[o.id] = Math.max(0, (counts[o.id] ?? 1) - 1);
          if (!was && now) counts[o.id] = (counts[o.id] ?? 0) + 1;
        }
        return { ...p, counts, my_votes: optionIds };
      };
      setByConv((prev) => ({
        ...prev,
        [convId]: (prev[convId] ?? []).map((m) => (m.id === messageId && m.poll ? { ...m, poll: applyOptimistic(m.poll) } : m)),
      }));
      try {
        const res = await conversationsApi.votePoll(convId, messageId, optionIds);
        setByConv((prev) => ({
          ...prev,
          [convId]: (prev[convId] ?? []).map((m) =>
            m.id === messageId && m.poll ? { ...m, poll: { ...m.poll, counts: res.counts, my_votes: res.my_votes } } : m,
          ),
        }));
      } catch {
        setByConv((prev) => ({ ...prev, [convId]: (prev[convId] ?? []).map((m) => (m.id === messageId ? msg : m)) }));
      }
    },
    [byConv],
  );

  // Toggle a reaction on a message (optimistic; reconciled with the server tally).
  // The actor sees it immediately; other viewers pick it up on the periodic reconcile
  // above (reactions carry no SSE event by design).
  const toggleReaction = useCallback(
    async (messageId: string, key: string) => {
      const convId = activeRef.current;
      if (!convId || messageId.startsWith('optimistic-')) return;
      const before = byConv[convId] ?? [];
      const msg = before.find((m) => m.id === messageId);
      if (!msg) return;
      const has = (msg.my_reactions ?? []).includes(key);
      const optimistic = (m: Message): Message => {
        const counts: Record<string, number> = { ...(m.reactions ?? {}) };
        const mine = new Set(m.my_reactions ?? []);
        if (has) {
          counts[key] = Math.max(0, (counts[key] ?? 1) - 1);
          if (counts[key] === 0) delete counts[key];
          mine.delete(key);
        } else {
          counts[key] = (counts[key] ?? 0) + 1;
          mine.add(key);
        }
        return { ...m, reactions: counts, my_reactions: [...mine] };
      };
      setByConv((prev) => ({ ...prev, [convId]: (prev[convId] ?? []).map((m) => (m.id === messageId ? optimistic(m) : m)) }));
      try {
        const res = has
          ? await conversationsApi.removeReaction(convId, messageId, key)
          : await conversationsApi.addReaction(convId, messageId, key);
        setByConv((prev) => ({
          ...prev,
          [convId]: (prev[convId] ?? []).map((m) =>
            m.id === messageId ? { ...m, reactions: res.reactions, my_reactions: res.my_reactions } : m,
          ),
        }));
      } catch {
        setByConv((prev) => ({ ...prev, [convId]: (prev[convId] ?? []).map((m) => (m.id === messageId ? msg : m)) }));
      }
    },
    [byConv],
  );

  // Edit your own text message (optimistic; reconciled with the server copy). Other
  // viewers pick it up on the periodic reconcile, same as reactions.
  const editMessage = useCallback(
    async (messageId: string, body: string) => {
      const convId = activeRef.current;
      const trimmed = body.trim();
      if (!convId || !trimmed || messageId.startsWith('optimistic-')) return;
      const before = byConv[convId] ?? [];
      const msg = before.find((m) => m.id === messageId);
      if (!msg || msg.body_plain === trimmed) return;
      const nowIso = new Date().toISOString();
      setByConv((prev) => ({
        ...prev,
        [convId]: (prev[convId] ?? []).map((m) => (m.id === messageId ? { ...m, body_plain: trimmed, edited_at: nowIso } : m)),
      }));
      try {
        const saved = await conversationsApi.editMessage(convId, messageId, trimmed);
        setByConv((prev) => ({ ...prev, [convId]: mergeMessage(prev[convId] ?? [], saved) }));
      } catch {
        // Roll back to the pre-edit copy on failure.
        setByConv((prev) => ({ ...prev, [convId]: (prev[convId] ?? []).map((m) => (m.id === messageId ? msg : m)) }));
      }
    },
    [byConv],
  );

  // Delete a message (sender, or OWNER/ADMIN). Optimistic hide; rolled back + toasted
  // on refusal. Other viewers drop it on their next reconcile.
  const deleteMessage = useCallback(
    async (messageId: string) => {
      const convId = activeRef.current;
      if (!convId || messageId.startsWith('optimistic-')) return;
      const before = byConv[convId] ?? [];
      const msg = before.find((m) => m.id === messageId);
      if (!msg) return;
      const nowIso = new Date().toISOString();
      setByConv((prev) => ({ ...prev, [convId]: (prev[convId] ?? []).map((m) => (m.id === messageId ? { ...m, deleted_at: nowIso } : m)) }));
      try {
        await conversationsApi.deleteMessage(convId, messageId);
      } catch (e) {
        toast.error(getApiErrorMessage(e, 'Message could not be deleted'));
        setByConv((prev) => ({ ...prev, [convId]: (prev[convId] ?? []).map((m) => (m.id === messageId ? msg : m)) }));
      }
    },
    [byConv],
  );

  // Star/unstar a conversation (optimistic; the inbox reflects it immediately, other
  // devices on their next inbox load).
  const toggleFavorite = useCallback(
    async (conversationId: string) => {
      let next = false;
      setInbox((prev) =>
        prev.map((c) => {
          if (c.id !== conversationId) return c;
          next = !c.is_favorite;
          return { ...c, is_favorite: next };
        }),
      );
      try {
        await conversationsApi.setFavorite(conversationId, next);
      } catch {
        setInbox((prev) => prev.map((c) => (c.id === conversationId ? { ...c, is_favorite: !next } : c)));
      }
    },
    [],
  );

  // Get a conversation off my list. No hard delete anywhere: "remove" only hides the
  // row for me (new activity unhides it), "leave" takes me out of a circle everyone
  // else keeps, "archive" closes a circle I own for everyone (history kept).
  const applyRowAction = useCallback(
    async (conversationId: string, action: RowAction) => {
      const existed = inboxRef.current.some((c) => c.id === conversationId);
      setInbox((prev) => prev.filter((c) => c.id !== conversationId));
      try {
        if (action === 'remove') await conversationsApi.setHidden(conversationId, true);
        else if (action === 'leave') await conversationsApi.leave(conversationId);
        else await conversationsApi.archive(conversationId);
      } catch (e) {
        // Put it back exactly as the server has it — the same refetch the screen
        // uses everywhere else, so the row returns in its proper place.
        if (existed) void loadInbox();
        throw e;
      }
    },
    [loadInbox],
  );

  /**
   * Fetch the page of messages before the oldest one held.
   *
   * ⚠️ Prepends only. The caller restores the scroll position, because growing
   * a list upwards moves everything down and would otherwise yank the thread
   * out from under whoever is reading it.
   */
  const loadOlder = useCallback(async () => {
    if (!activeId) return;
    const h = history[activeId] ?? initialHistory;
    if (!shouldLoadOlder(h, true)) return;
    setHistory((prev) => ({ ...prev, [activeId]: { ...h, loading: true } }));
    try {
      const page = await conversationsApi.messages(activeId, { beforeSeq: h.cursor, limit: PAGE });
      const asc = [...page.messages].sort((a, b) => a.seq - b.seq);
      setByConv((prev) => {
        const cur = prev[activeId] ?? [];
        const seen = new Set(cur.map((m) => m.id));
        const older = asc.filter((m) => !seen.has(m.id));
        return { ...prev, [activeId]: [...older, ...cur] };
      });
      setHistory((prev) => ({ ...prev, [activeId]: afterPage(page.next_before_seq) }));
    } catch {
      // Let them try again by scrolling; leaving `loading` true would wedge it.
      setHistory((prev) => ({ ...prev, [activeId]: { ...h, loading: false } }));
    }
  }, [activeId, history]);

  return {
    inbox,
    inboxLoading,
    loadOlder,
    /** Whether there is more history to fetch for the open conversation. */
    canLoadOlder: !!activeId && shouldLoadOlder(history[activeId] ?? initialHistory, true),
    loadingOlder: !!activeId && (history[activeId]?.loading ?? false),
    activeId,
    setActiveId: () => undefined, // navigation is URL-driven (see the page)
    messages: activeId ? byConv[activeId] ?? [] : [],
    messagesLoading,
    // Aggregate receipt cursors for the open conversation. Slice 3 renders the
    // ticks from this; undefined simply means "not known yet", which is also
    // the honest state for messages that predate the feature.
    receipts: activeId ? receipts[activeId] : undefined,
    streamStatus,
    sendMessage,
    sendGif,
    sendPoll,
    sendFile,
    createAssignment,
    votePoll,
    toggleReaction,
    editMessage,
    deleteMessage,
    toggleFavorite,
    applyRowAction,
    reload: loadInbox,
  };
}
